import { Button, cn, Spinner, Stack } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentType, Conversation, Message } from '@/api/rest/resources/types/conversation';
import { ConversationPreview, NewConversation } from '@/components/CoPilot/components/AIAgent';
import { AnnotationsPanel } from '@/components/CoPilot/components/Annotations';
import { ConversationTitle, CurrentChat } from '@/components/CoPilot/components/Chat';
import { CoPilotLogo } from '@/components/CoPilot/components/CoPilot';
import { useGlobalConversations } from '@/components/CoPilot/hooks/useGlobalConversations';
import { useOrganizationConversations } from '@/components/CoPilot/hooks/useOrganizationConversations';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useUser } from '@/lib/auth';
import { isNullish } from '@/utils/booleans';

enum ConversationTab {
  all = 'all',
  user = 'user',
  annotations = 'annotations',
}

export const CopilotConversationsPage = () => {
  const user = useUser();

  const [activeTab, setActiveTab] = useState<ConversationTab>(ConversationTab.all);

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [chatKey, setChatKey] = useState<number>(2);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const handleSelectAnnotation = (message: Message | null) => {
    if (isNullish(message)) {
      setActiveConversationId(null);
      setSelectedMessageId(null);
      return;
    }
    setActiveConversationId(message.conversation_id);
    setSelectedMessageId(message.id);
  };

  const handleSelectConversation = (id: string | null) => {
    setActiveConversationId(id);
    setSelectedMessageId(null);
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setSelectedMessageId(null);
    setChatKey((k) => k + 1);
  };

  return (
    <div className='flex h-[calc(100vh-180px)] min-h-[500px] overflow-hidden rounded-xl border border-neutral-black-12'>
      <Stack className='flex flex-1 flex-col overflow-hidden'>
        <CurrentConversationPanelHeader
          activeConversationId={activeConversationId}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNewChat={handleNewChat}
        />
        <Stack className='p-5 flex-1 overflow-hidden flex flex-col'>
          <CurrentChat
            key={`${chatKey}-${activeConversationId}`}
            activeAgentType={AgentType.copilot}
            conversationIdArg={activeConversationId}
            highlightedMessageId={selectedMessageId}
          />
        </Stack>
      </Stack>
      <ConversationsPanel
        activeTab={activeTab}
        onTabChange={setActiveTab}
        activeConversationId={activeConversationId}
        currentUserId={user?.email ?? null}
        onSelectConversation={handleSelectConversation}
        selectedMessageId={selectedMessageId}
        onSelectAnnotation={handleSelectAnnotation}
      />
    </div>
  );
};

export const CurrentConversationPanelHeader = withSuspenseBoundary(
  ({
    activeConversationId,
    activeTab,
    setActiveTab,
    onNewChat,
  }: {
    activeConversationId: string | null;
    activeTab: ConversationTab;
    setActiveTab: (tab: ConversationTab) => void;
    onNewChat?: () => void;
  }) => {
    const { t } = useTranslation();

    return (
      <div className='flex h-[60px] items-center justify-between border-b border-neutral-black-12 px-3 py-1.5 shrink-0'>
        <Stack className='flex flex-row items-center gap-2'>
          <CoPilotLogo showText={false} />
          <span className='max-w-md truncate typography-body1 text-text-primary'>
            {isNullish(activeConversationId) ? (
              t('global.copilot.conversation.newChat')
            ) : (
              <ConversationTitle conversationId={activeConversationId} />
            )}
          </span>
        </Stack>

        <Stack direction='row' className='items-center gap-2'>
          {!isNullish(activeConversationId) && (
            <Button
              data-testid='conversation-annotations-toggle'
              title={t('global.copilot.annotations.title')}
              variant='outline'
              className={cn(
                'border-none typography-button1 text-text-primary rounded-full w-8 hover:bg-primary-30 aspect-auto',
                activeTab === ConversationTab.annotations ? 'bg-primary-30' : '',
              )}
              onClick={() =>
                activeTab === ConversationTab.annotations
                  ? setActiveTab(ConversationTab.all)
                  : setActiveTab(ConversationTab.annotations)
              }
              leftAdornment={<Icon icon='ri-feedback-line' />}
            />
          )}
          {onNewChat && <NewConversation onClick={onNewChat} />}
        </Stack>
      </div>
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

export const ConversationsPanel = withSuspenseBoundary(
  ({
    activeTab,
    onTabChange,
    activeConversationId,
    currentUserId,
    onSelectConversation,
    selectedMessageId,
    onSelectAnnotation,
    showGlobal = false,
  }: {
    activeTab: ConversationTab;
    onTabChange: (tab: ConversationTab) => void;
    activeConversationId: string | null;
    currentUserId: string | null;
    onSelectConversation: (id: string | null, organizationId?: string | null) => void;
    selectedMessageId: string | null;
    onSelectAnnotation: (message: Message | null) => void;
    showGlobal?: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <Stack className='flex w-96 shrink-0 border-l border-neutral-black-12'>
        <Stack className='flex w-fit h-[60px] flex-row border-b border-neutral-black-12 shrink-0 justify-between'>
          {Object.keys(ConversationTab).map((tab) => (
            <Button
              key={tab}
              variant='outline'
              onClick={() => onTabChange(ConversationTab[tab as keyof typeof ConversationTab])}
              className={cn(
                'w-fit rounded-none border-x-0 border-y-0 text-text-primary',
                activeTab === ConversationTab[tab as keyof typeof ConversationTab] &&
                  'border-b-4 border-b-primary-100 font-semibold',
              )}
            >
              {t(`global.copilot.conversation.conversations.tabs.${tab as keyof typeof ConversationTab}`)}
            </Button>
          ))}
        </Stack>
        <Stack className='overflow-y-auto'>
          {activeTab !== ConversationTab.annotations && (
            <AllPreviousConversations
              activeTab={activeTab}
              currentUserId={currentUserId}
              activeConversationId={activeConversationId}
              onSelectConversation={onSelectConversation}
              showGlobal={showGlobal}
            />
          )}

          {activeTab === ConversationTab.annotations && (
            <AnnotationsPanel
              conversationId={activeConversationId}
              selectedMessageId={selectedMessageId}
              onSelectAnnotation={onSelectAnnotation}
              showGlobal={showGlobal}
            />
          )}
        </Stack>
      </Stack>
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

const AllPreviousConversations = withSuspenseBoundary(
  ({
    activeTab,
    activeConversationId,
    currentUserId,
    onSelectConversation,
    showGlobal = false,
  }: {
    activeTab: ConversationTab.all | ConversationTab.user;
    activeConversationId: string | null;
    currentUserId: string | null;
    onSelectConversation: (id: string | null, organizationId?: string | null) => void;
    showGlobal?: boolean;
  }) => {
    const PreviousConversationsComponent = showGlobal ? GlobalPreviousConversations : OrganizationPreviousConversations;
    return (
      <PreviousConversationsComponent
        activeTab={activeTab}
        activeConversationId={activeConversationId}
        onSelectConversation={onSelectConversation}
        currentUserId={currentUserId}
      />
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

const GlobalPreviousConversations = withSuspenseBoundary(
  ({
    activeTab,
    activeConversationId,
    onSelectConversation,
    currentUserId,
  }: {
    activeTab: ConversationTab.all | ConversationTab.user;
    activeConversationId: string | null;
    onSelectConversation: (id: string | null, organizationId?: string | null) => void;
    currentUserId?: string | null;
  }) => {
    const allConversations = useGlobalConversations().data;
    const userConversations = useGlobalConversations(currentUserId ? { created_by: currentUserId } : undefined).data;

    return (
      <PreviousConversations
        activeTab={activeTab}
        activeConversationId={activeConversationId}
        onSelectConversation={onSelectConversation}
        allConversations={allConversations ?? []}
        userConversations={userConversations ?? []}
      />
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

export const OrganizationPreviousConversations = withSuspenseBoundary(
  ({
    activeTab,
    activeConversationId,
    onSelectConversation,
    currentUserId,
  }: {
    activeTab: ConversationTab.all | ConversationTab.user;
    activeConversationId: string | null;
    onSelectConversation: (id: string | null, organizationId?: string | null) => void;
    currentUserId: string | null;
  }) => {
    const allConversations = useOrganizationConversations().data;
    const userConversations = useOrganizationConversations(
      undefined,
      currentUserId ? { created_by: currentUserId } : undefined,
    ).data;

    return (
      <PreviousConversations
        activeTab={activeTab}
        activeConversationId={activeConversationId}
        onSelectConversation={onSelectConversation}
        allConversations={allConversations ?? []}
        userConversations={userConversations ?? []}
      />
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

const PreviousConversations = withSuspenseBoundary(
  ({
    activeTab,
    activeConversationId,
    onSelectConversation,
    allConversations,
    userConversations,
  }: {
    activeTab: ConversationTab.all | ConversationTab.user;
    activeConversationId: string | null;
    onSelectConversation: (id: string | null, organizationId?: string | null) => void;
    allConversations: Conversation[];
    userConversations: Conversation[];
  }) => {
    const { t } = useTranslation();

    const conversations = activeTab === ConversationTab.all ? allConversations : userConversations;

    return (
      <Stack className='overflow-y-auto'>
        {conversations.map((conversation) => (
          <ConversationPreview
            key={conversation.id}
            conversation={conversation}
            isSelected={activeConversationId === conversation.id}
            onClick={() =>
              activeConversationId === conversation.id
                ? onSelectConversation(null, null)
                : onSelectConversation(conversation.id, conversation.organization)
            }
          />
        ))}
        {conversations.length === 0 && (
          <span className='typography-body1 text-text-secondary text-center mt-4'>
            {t('global.copilot.conversation.noConversationHistory')}
          </span>
        )}
      </Stack>
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);
